import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirebaseAuth } from '../lib/firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import GoogleIcon from '../components/icons/GoogleIcon';

const AuthScreen: React.FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    const provider = new GoogleAuthProvider();
    try {
      const auth = getFirebaseAuth();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();

      // Send the ID token to your backend for verification
      const response = await fetch('/api/auth/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Backend authentication successful:', data);
        // On successful backend authentication, onAuthStateChanged in App.tsx will still handle the rest.
      } else {
        setError(data.message || 'Backend authentication failed');
      }
    } catch (err: any) {
      if (err.code && err.code === 'auth/popup-closed-by-user') {
        // Do nothing, user intentionally closed the popup.
      } else if (err.code && err.message) {
        setError(t('auth_screen.sign_in_failed', { message: err.message }));
      } else {
        setError(t('auth_screen.unexpected_error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-serif font-bold text-accent">{t('app.title')}</h1>
          <p className="text-text-secondary mt-2">{t('auth_screen.sign_in_to_continue')}</p>
        </div>

        <div className="bg-background-secondary p-8 rounded-lg shadow-lg">
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full bg-ui-element text-text-primary font-semibold py-3 px-4 rounded-lg hover:bg-ui-border transition-colors disabled:bg-opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-ui-border border-t-accent rounded-full animate-spin"></div>
            ) : (
              <>
                <GoogleIcon className="w-6 h-6" />
                <span>{t('auth_screen.sign_in_with_google')}</span>
              </>
            )}
          </button>
          {error && <p className="mt-4 text-sm text-red-400 text-center">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
